import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, Users, Activity, Target, AlertTriangle, XCircle, UserMinus, ArrowUpRight, UserCheck, UserX, Percent, UsersRound, Wallet, ChevronDown, ChevronRight, DollarSign, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getWorkspaceDashboard } from "@/lib/dashboard.functions";
import { getProjectedMrr } from "@/lib/payment-schedules.functions";
import { StuckImportAlert } from "@/components/stuck-import-alert";
import { UnclassifiedProductsBanner } from "@/components/unclassified-products-banner";
import { formatCurrency } from "@/lib/utils";
import { MetricInfo, METRIC_INFO } from "@/components/metric-info";
import { Badge } from "@/components/ui/badge";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/")({
  component: WorkspaceDashboard,
});

const PERIODS = ["today", "week", "last_4_weeks", "month", "quarter", "year", "all"] as const;
type Period = (typeof PERIODS)[number];

const PERIOD_LABELS: Record<Period, string> = {
  today: "Today",
  week: "This week",
  last_4_weeks: "Last 4 weeks",
  month: "This month",
  quarter: "This quarter",
  year: "This year",
  all: "All time",
};

// Compute the calendar range in America/New_York, then express bounds as UTC instants.
// Stored transaction_date values are calendar-date UTC-midnight (e.g. 2026-05-01 00:00+00).
// Using a NY-offset filter excludes those rows, so we anchor on calendar dates only.
function nyParts(d: Date): { y: number; m: number; day: number } {
  const f = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" });
  const [y, m, day] = f.format(d).split("-").map(Number);
  return { y, m, day };
}
function utcDate(y: number, m: number, d: number, endOfDay = false): Date {
  return new Date(endOfDay ? Date.UTC(y, m - 1, d, 23, 59, 59, 999) : Date.UTC(y, m - 1, d, 0, 0, 0, 0));
}
function computeRange(period: Period): { from: Date; to: Date } {
  const now = new Date();
  const { y, m, day } = nyParts(now);
  const todayStart = utcDate(y, m, day);
  const todayEnd = utcDate(y, m, day, true);
  switch (period) {
    case "today":
      return { from: todayStart, to: todayEnd };
    case "last_4_weeks": {
      const from = new Date(todayStart);
      from.setUTCDate(from.getUTCDate() - 27);
      return { from, to: todayEnd };
    }
    case "week": {
      // Mon..Sun calendar week in NY
      const dow = (new Date(Date.UTC(y, m - 1, day)).getUTCDay() + 6) % 7; // 0 = Mon
      const from = new Date(todayStart);
      from.setUTCDate(from.getUTCDate() - dow);
      const to = new Date(from);
      to.setUTCDate(to.getUTCDate() + 6);
      to.setUTCHours(23, 59, 59, 999);
      return { from, to };
    }
    case "month": {
      const from = utcDate(y, m, 1);
      const to = utcDate(y, m + 1, 0, true); // day 0 of next month = last day of this month
      return { from, to };
    }
    case "quarter": {
      const qStart = Math.floor((m - 1) / 3) * 3 + 1;
      const from = utcDate(y, qStart, 1);
      const to = utcDate(y, qStart + 3, 0, true);
      return { from, to };
    }
    case "year":
      return { from: utcDate(y, 1, 1), to: utcDate(y, 12, 31, true) };
    case "all":
      return { from: utcDate(2000, 1, 1), to: todayEnd };
  }
}


function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function WorkspaceDashboard() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/" });
  const fetchDash = useServerFn(getWorkspaceDashboard);
  const fetchProjMrr = useServerFn(getProjectedMrr);
  const [period, setPeriod] = useState<Period>("month");
  const [showDiag, setShowDiag] = useState(false);

  const range = useMemo(() => {
    const { from, to } = computeRange(period);
    return { from: from.toISOString(), to: to.toISOString(), fromMs: from.getTime(), toMs: to.getTime() };
  }, [period]);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", workspaceId, period],
    queryFn: () => fetchDash({ data: { workspace_id: workspaceId, from: range.from, to: range.to } }),
  });
  const { data: projData } = useQuery({
    queryKey: ["projected-mrr", workspaceId],
    queryFn: () => fetchProjMrr({ data: { workspace_id: workspaceId, days: 30 } }),
  });

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const m = (data?.metrics ?? {}) as any;
  const v2 = (data?.metricsV2 ?? {}) as any;
  const breakdown = (v2.breakdown ?? {}) as any;
  const series = (m.mrr_series ?? []) as Array<{ date: string; value: number }>;
  const byTier = (m.by_tier ?? []) as Array<{ tier: string; revenue: number }>;

  const revenue = Number(m.total_revenue ?? 0);
  const prevRevenue = Number(m.prev_total_revenue ?? 0);
  const revenueDelta = prevRevenue > 0 ? ((revenue - prevRevenue) / prevRevenue) * 100 : null;
  const deltaUp = revenueDelta !== null && revenueDelta >= 0;

  // Top row: 4 headline tiles
  const headlineTiles: Array<{ label: string; value: string; icon: any; infoKey: keyof typeof METRIC_INFO; sub?: React.ReactNode }> = [
    {
      label: `Revenue (vs prior ${PERIOD_LABELS[period].toLowerCase()})`,
      value: formatCurrency(revenue),
      icon: DollarSign,
      infoKey: "revenue",
      sub: revenueDelta === null ? (
        <span className="text-muted-foreground">No prior data</span>
      ) : (
        <span className={deltaUp ? "text-emerald-500" : "text-red-500"}>
          {deltaUp ? <TrendingUp className="inline h-3 w-3 mr-1" /> : <TrendingDown className="inline h-3 w-3 mr-1" />}
          {deltaUp ? "+" : ""}{revenueDelta.toFixed(1)}% vs {formatCurrency(prevRevenue)}
        </span>
      ),
    },
    { label: "MRR", value: formatCurrency(m.mrr ?? 0), icon: Wallet, infoKey: "mrr" },
    { label: "2nd Tier MRR", value: formatCurrency(v2.second_tier_mrr ?? 0), icon: Wallet, infoKey: "second_tier_mrr" },
    { label: "Total Active", value: String(v2.total_active ?? 0), icon: Users, infoKey: "total_active" },
  ];

  type Tile = { label: string; value: string; icon: any; infoKey: keyof typeof METRIC_INFO; tone?: "warn" | "danger" };
  const middleRow: Tile[] = [
    { label: "Active Monthly", value: String(v2.active_monthly ?? 0), icon: UserCheck, infoKey: "active_monthly" },
    { label: "Free Trials", value: String(v2.free_trials ?? 0), icon: Activity, infoKey: "free_trials" },
    { label: "Upgrades", value: String(v2.upgrades ?? 0), icon: ArrowUpRight, infoKey: "upgrades" },
    { label: "Upgrade Rate", value: `${v2.upgrade_rate ?? 0}%`, icon: Target, infoKey: "upgrade_rate" },
    { label: "At Risk", value: String(v2.at_risk ?? 0), icon: AlertTriangle, infoKey: "at_risk", tone: "warn" },
  ];
  const bottomRow: Tile[] = [
    { label: "Contacts", value: String(v2.contacts ?? 0), icon: UsersRound, infoKey: "contacts" },
    { label: "2nd Tier Affiliates", value: String(v2.second_tier_affiliates ?? 0), icon: UserX, infoKey: "second_tier_affiliates" },
    { label: "Canceled Trial", value: String(v2.canceled_trial ?? 0), icon: XCircle, infoKey: "canceled_trial", tone: "danger" },
    { label: "Trial Churned", value: String(v2.trial_churned ?? 0), icon: UserMinus, infoKey: "trial_churned", tone: "danger" },
    { label: "Churn Rate", value: `${v2.churn_rate ?? 0}%`, icon: Percent, infoKey: "churn_rate", tone: "warn" },
  ];

  return (
    <div className="space-y-6">
      <StuckImportAlert workspaceId={workspaceId} />
      <UnclassifiedProductsBanner workspaceId={workspaceId} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{m.revenue_model ?? ""}</span>
          {v2.exclusion_rules_source && (
            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Badge variant={v2.exclusion_rules_source === "metric_definitions" ? "default" : "outline"}>
                    Metric source: {v2.exclusion_rules_source}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {v2.exclusion_rules_source === "metric_definitions"
                    ? "Bad-trial rules are saved on this workspace and driving metric calculations."
                    : "No saved bad-trial rules, using built-in fallback defaults. Save rules in Settings to switch."}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
        <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => <SelectItem key={p} value={p}>{PERIOD_LABELS[p]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {headlineTiles.map((t) => (
          <Card key={t.label} className="border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <MetricInfo infoKey={t.infoKey} />
                <t.icon className="h-4 w-4 text-primary" />
              </div>
              <div className="text-2xl font-semibold">{t.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.label}</div>
              {t.sub && <div className="text-xs mt-2">{t.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {middleRow.map((t) => (
          <Card key={t.label} className={t.tone === "warn" ? "border-amber-500/40" : t.tone === "danger" ? "border-red-500/40" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <MetricInfo infoKey={t.infoKey} />
                <t.icon className={`h-4 w-4 ${t.tone === "warn" ? "text-amber-500" : t.tone === "danger" ? "text-red-500" : "text-muted-foreground"}`} />
              </div>
              <div className="text-2xl font-semibold">{t.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {bottomRow.map((t) => (
          <Card key={t.label} className={t.tone === "warn" ? "border-amber-500/40" : t.tone === "danger" ? "border-red-500/40" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <MetricInfo infoKey={t.infoKey} />
                <t.icon className={`h-4 w-4 ${t.tone === "warn" ? "text-amber-500" : t.tone === "danger" ? "text-red-500" : "text-muted-foreground"}`} />
              </div>
              <div className="text-2xl font-semibold">{t.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MetricInfo infoKey="daily_commissions" />Daily Commissions</CardTitle></CardHeader>
          <CardContent>
            {(() => {
              const DAY = 86400000;
              const valueByDay = new Map<string, number>();
              for (const s of series) {
                const key = new Date(s.date).toISOString().slice(0, 10);
                valueByDay.set(key, (valueByDay.get(key) ?? 0) + Number(s.value || 0));
              }
              // Effective end: today (NY-calendar UTC midnight) when within range; otherwise range end.
              const nyToday = (() => {
                const f = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" });
                const [y, mo, d] = f.format(new Date()).split("-").map(Number);
                return Date.UTC(y, mo - 1, d);
              })();
              let endMs = Math.min(range.toMs, nyToday);
              if (endMs < range.fromMs) endMs = range.fromMs;
              // For "This month", keep a minimum 15-day window so early-month bars don't crowd the left.
              if (period === "month") {
                const minEnd = range.fromMs + 14 * DAY;
                if (endMs < minEnd) endMs = Math.min(minEnd, range.toMs);
              }
              const buckets: Array<{ date: string; label: string; value: number }> = [];
              for (let t = range.fromMs; t <= endMs; t += DAY) {
                const iso = new Date(t).toISOString().slice(0, 10);
                buckets.push({
                  date: iso,
                  label: new Date(t).toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric" }),
                  value: valueByDay.get(iso) ?? 0,
                });
              }
              if (buckets.every(b => b.value === 0)) {
                return <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No commissions in this period</div>;
              }
              return (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={buckets} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" className="text-xs" interval="preserveStartEnd" minTickGap={16} />
                    <YAxis className="text-xs" width={72} tickFormatter={(v) => formatCurrency(v)} />
                    <Tooltip formatter={(v: any) => formatCurrency(v)} />
                    <Bar dataKey="value" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MetricInfo infoKey="revenue_by_tier" />Revenue By Tier</CardTitle></CardHeader>
          <CardContent>
            {byTier.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byTier}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="tier" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(v: any) => formatCurrency(v)} />
                  <Bar dataKey="revenue" fill="var(--primary)" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No tier data</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <button onClick={() => setShowDiag((s) => !s)} className="flex items-center gap-2 hover:text-primary">
              {showDiag ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Metric diagnostics
            </button>
          </CardTitle>
        </CardHeader>
        {showDiag && (
          <CardContent>
            <div className="text-xs text-muted-foreground mb-3">Timezone: {v2.timezone} · Computed: {v2.computed_at ? new Date(v2.computed_at).toLocaleString() : ","}</div>
            <div className="space-y-2 text-sm">
              <DiagRow title={`Active Monthly: ${v2.active_monthly}`}>
                Started with {breakdown.active_monthly?.initial_unique_emails_all_time ?? breakdown.active_monthly?.initial_unique_emails_35d ?? 0} unique commission emails (all-time).
                Removed {breakdown.active_monthly?.removed_churned ?? 0} churned,
                {" "}{breakdown.active_monthly?.removed_canceled ?? 0} canceled,
                {" "}{breakdown.active_monthly?.removed_voided ?? 0} voided,
                {" "}{breakdown.active_monthly?.removed_refunded ?? 0} refunded.
                Final: {breakdown.active_monthly?.final ?? 0}.
              </DiagRow>
              <DiagRow title={`Total Active: ${v2.total_active}`}>
                Active Monthly ({breakdown.total_active?.active_monthly ?? 0}) + Upgrades ({breakdown.total_active?.plus_upgrades ?? 0}) = {breakdown.total_active?.total ?? 0}. No dedupe, additive.
              </DiagRow>
              <DiagRow title={`Upgrades: ${v2.upgrades}`}>
                Unique emails all-time with non-cancelled commission in $1,198.80 / $2,160 / $480.
              </DiagRow>
              <DiagRow title={`Trial Churned: ${v2.trial_churned}`}>
                {breakdown.trial_churned?.churned_trial_emails ?? 0} churned trial emails − {breakdown.trial_churned?.excluded_upgraded ?? 0} excluded as upgraded = {breakdown.trial_churned?.final ?? 0}.
              </DiagRow>
              <DiagRow title={`Upgrade Rate: ${v2.upgrade_rate}%`}>
                {breakdown.upgrade_rate?.upgrades ?? 0} upgrades / {breakdown.upgrade_rate?.total_trials_all_time ?? 0} total trials × 100.
              </DiagRow>
              <DiagRow title={`Churn Rate: ${v2.churn_rate}%`}>
                ({breakdown.churn_rate?.trial_churned_90d ?? 0} trial churned + {breakdown.churn_rate?.canceled_trial_90d ?? 0} canceled trial + {breakdown.churn_rate?.commission_cancelled_90d ?? 0} commission cancelled in last 90d) / {breakdown.churn_rate?.denominator_trials_90d ?? 0} trials started in last 90d × 100.
              </DiagRow>
            </div>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
        <CardContent>
          {(data?.recent ?? []).length === 0 ? (
            <div className="text-sm text-muted-foreground">Nothing yet.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {data!.recent.map((e: any) => (
                <li key={e.id} className="flex items-center justify-between">
                  <span>{e.description}</span>
                  <span className="text-xs text-muted-foreground">{new Date(e.occurred_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DiagRow({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-primary/40 pl-3 py-1">
      <div className="font-medium">{title}</div>
      <div className="text-xs text-muted-foreground">{children}</div>
    </div>
  );
}

